import React, { useCallback, useEffect, useState } from "react";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import { SectionRule } from "components/common/SectionRule";
import { Inline } from "components/common/Stack";
import { TooltipButton } from "components/common/TooltipButton";
import { formatDayMonthYear, formatShortDate } from "@/lib/dates";
import { formatBytes } from "@/lib/format";
import {
  addSeats,
  fetchBillingDetails,
  previewBillingContact,
  type Org,
} from "@/lib/api/organizations";
import { confirmCardChange } from "@/stores/usePendingCheckoutStore";
import { useDialog } from "components/hooks/useDialog";
import { useBillingContext } from "components/main/organizations/useBillingContext";
import { useBillingActions } from "components/main/organizations/useBillingActions";
import {
  canBuySeats,
  useSeatPurchase,
} from "components/main/organizations/useSeatPurchase";
import { SeatsDialog } from "components/main/organizations/SeatsDialog";
import { CancelSubscriptionDialog } from "components/main/organizations/CancelSubscriptionDialog";
import { BillingDetailsDialog } from "components/main/organizations/BillingDetailsDialog";
import { CardFormDialog } from "components/main/organizations/CardFormDialog";
import { StandingNotice } from "components/main/organizations/StandingNotice";
import {
  DoubleBilledNotice,
  NoCardNotice,
  PayerLeftNotice,
  PaymentApplyingNotice,
  PaymentStuckNotice,
  PendingSeatsNotice,
  SeatShortfallNotice,
} from "components/main/organizations/BillingNotices";
import {
  UnattachedSubscriptions,
  type AttachResult,
} from "components/main/organizations/UnattachedSubscriptions";
import { PlanSummary } from "components/main/organizations/PlanSummary";
import { PlanUpgrade } from "components/main/organizations/PlanUpgrade";
import { UsageSection } from "components/main/organizations/UsageSection";
import { InvoiceHistory } from "components/main/organizations/InvoiceHistory";
import { seatRate } from "components/main/organizations/seatRate";

export const PlanUsagePanel: React.FC<{
  org: Org;
  memberCount: number;
}> = ({ org, memberCount }) => {
  const {
    plan,
    planRead,
    billing,
    usage,
    catalog,
    applying,
    stuck,
    watchExpired,
    recheck,
    confirmedAt,
    patchBilling,
    refreshBillingContext,
    refreshUsage,
    reloadBilling,
  } = useBillingContext(org.org_id);
  const {
    error: billingError,
    setError: setBillingError,
    cardForm,
    openCard,
    openPortal,
    details,
    openDetails,
    saveDetails,
    cancelDialog,
    openCancel,
    changeSubscription,
  } = useBillingActions({
    orgId: org.org_id,
    billing,
    patchBilling,
    reloadBilling,
    refreshBillingContext,
  });
  const plans = useDialog();
  /// What the attach door answered when the roster outgrew the seats bought.
  const [seatShortfall, setSeatShortfall] = useState<{
    members: number;
    seats: number;
  } | null>(null);

  const onAttached = useCallback(
    (result: AttachResult) => {
      // The door's count, never a second one; kept, not toasted, because the
      // state outlives a toast.
      setSeatShortfall(result.over_seats ? result : null);
      reloadBilling();
    },
    [reloadBilling],
  );

  const loadBillingDetails = useCallback(
    () => fetchBillingDetails(org.org_id),
    [org.org_id],
  );
  const previewContact = useCallback(
    (email: string) => previewBillingContact(org.org_id, email),
    [org.org_id],
  );

  const seatCap = plan?.limits.seats ?? null;
  // Compared on price: `sort_order` orders the display and claims nothing about grants.
  const upgrades = catalog
    .filter((row) => row.price_cents > (plan?.price_cents ?? 0))
    .sort((a, b) => a.sort_order - b.sort_order);
  // An org on a plan the catalog no longer lists has no "current" card.
  const currentRow = catalog.find((row) => row.code === plan?.code) ?? null;

  const pendingCancel =
    billing?.subscription?.scheduled_change?.action === "cancel";
  /// A cycle Paddle could not collect; survives a scheduled cancellation.
  const pastDue = billing?.subscription?.past_due_since != null;

  /// Paused at Paddle: the org sits on the free tier with the subscription
  /// still there.
  const paused = billing?.subscription?.status === "paused";

  /// Card, cancel and resume — never the portal, which is the payer's.
  const canActOnSubscription = Boolean(
    billing?.can_manage_billing && billing?.subscription,
  );
  const canOpenPortal = Boolean(
    billing?.can_manage_billing && billing?.can_open_portal,
  );
  /// Not the customer id: a cancelled org keeps its invoices after its row goes.
  const canSeeInvoices = Boolean(
    billing?.can_manage_billing && billing?.has_billing_history,
  );

  const heldSeats = billing?.subscription?.seats ?? 0;
  const rate = seatRate(plan, billing?.subscription);
  const canAddSeats = canBuySeats(billing) && !org.is_personal;
  // A reduction is parked for the boundary, so it needs a boundary to wait for.
  const canReduceSeats =
    canAddSeats && billing?.subscription?.current_period_end != null;
  const seatPurchase = useSeatPurchase(
    org.org_id,
    heldSeats,
    memberCount,
    canReduceSeats,
  );

  /// The door answers once the period has moved, or the reduction is parked.
  const confirmSeats = useCallback(() => {
    void seatPurchase.confirm().then((bought) => {
      if (bought !== null) reloadBilling();
    });
  }, [seatPurchase, reloadBilling]);

  // Cleared by the fix landing, never by a timer: the period is re-read after a
  // seat change, and the cap covering the roster is what makes this untrue.
  useEffect(() => {
    if (seatShortfall && seatCap !== null && seatCap >= memberCount) {
      setSeatShortfall(null);
    }
  }, [seatShortfall, seatCap, memberCount]);

  const pendingSeats = billing?.subscription?.pending_seats ?? null;
  const cancelPendingSeats = useCallback(() => {
    void addSeats(org.org_id, heldSeats).then(() => refreshBillingContext());
  }, [org.org_id, heldSeats, refreshBillingContext]);

  const periodEnd = billing?.subscription?.current_period_end;
  const cycleDate =
    periodEnd != null ? formatShortDate(new Date(periodEnd * 1000)) : null;

  // Never on a failed read: every paid row would look like an upgrade.
  const offersUpgrade =
    planRead === "loaded" &&
    upgrades.length > 0 &&
    !applying &&
    !stuck &&
    !paused;
  const canEditBillingDetails = Boolean(
    billing?.can_manage_billing && billing?.billed_to && billing?.subscription,
  );
  // D58's courtesy: cancelling strands an org that already holds more than the
  // free tier. A missing free row or ceiling means we cannot say, so we do not.
  const freeStorageBytes =
    catalog.find((row) => row.price_cents === 0)?.limits.storage_bytes ?? null;
  const overFreeLimit =
    usage && freeStorageBytes !== null && usage.storage.used > freeStorageBytes
      ? {
          held: formatBytes(usage.storage.used),
          allowed: formatBytes(freeStorageBytes),
        }
      : null;

  return (
    <>
      {billing?.can_manage_billing && !billing.subscription && (
        <UnattachedSubscriptions
          orgId={org.org_id}
          memberCount={memberCount}
          onAttached={onAttached}
          onError={setBillingError}
        />
      )}
      {usage && (
        <StandingNotice
          standing={usage.standing}
          onUpdateCard={
            canActOnSubscription ? () => void openCard() : undefined
          }
        />
      )}
      {billing?.subscription?.double_billed &&
        billing.subscription.subscription_id && (
          <DoubleBilledNotice
            subscriptionId={billing.subscription.subscription_id}
          />
        )}
      {seatShortfall && (
        <SeatShortfallNotice
          shortfall={seatShortfall}
          onAddSeats={canAddSeats ? seatPurchase.openEditor : null}
        />
      )}
      {pendingSeats && (
        <PendingSeatsNotice
          pending={pendingSeats}
          heldSeats={heldSeats}
          onKeep={canActOnSubscription ? cancelPendingSeats : null}
        />
      )}
      <section className="flex flex-col">
        <SectionRule label="Plan" />
        {billing?.card_on_file === false &&
          billing.can_manage_billing &&
          billing.subscription && (
            <NoCardNotice
              cycleDate={cycleDate}
              onAddCard={() => void openCard()}
            />
          )}
        {billing?.payer_left && billing.can_manage_billing && (
          <PayerLeftNotice
            billedTo={billing.billed_to}
            onUpdateCard={canActOnSubscription ? () => void openCard() : null}
            onEditDetails={openDetails}
          />
        )}
        {stuck && !applying && <PaymentStuckNotice />}
        {applying && (
          <PaymentApplyingNotice
            watchExpired={watchExpired}
            onRecheck={recheck}
          />
        )}
        <PlanSummary
          org={org}
          plan={plan}
          seatRate={rate}
          planRead={planRead}
          currentRow={currentRow}
          memberCount={memberCount}
          standing={usage?.standing.state ?? "current"}
          pendingCancel={pendingCancel}
          paused={paused}
          cycleDate={cycleDate}
          offersUpgrade={offersUpgrade}
          // Unknown while the context loads: the pre-flight refuses a non-owner anyway.
          canUpgrade={billing?.can_manage_billing !== false}
          onUpgrade={() => plans.show(true)}
        />
        {/* Every act here is in-app; the portal link sits on the Invoices rule. */}
        {canActOnSubscription && (
          <Inline gap="sm" className="px-2 py-1">
            {canAddSeats && (
              <TooltipButton
                title={
                  canReduceSeats
                    ? "Change how many seats this organization pays for. More are available at once; fewer take effect when the cycle renews."
                    : "Buy more seats for this organization."
                }
                variant="secondary"
                size="sm"
                icon={<PersonAddAltOutlinedIcon className="icon-sm" />}
                onClick={seatPurchase.openEditor}
                data-testid="add-seats"
              >
                {canReduceSeats ? "Change seats" : "Add seats"}
              </TooltipButton>
            )}
            {canEditBillingDetails && (
              <TooltipButton
                title="The card, where invoices are sent, and the company and tax number they carry."
                variant="secondary"
                size="sm"
                icon={<CreditCardOutlinedIcon className="icon-sm" />}
                onClick={openDetails}
                data-testid="billing-details"
              >
                Billing details
              </TooltipButton>
            )}
            {pendingCancel ? (
              <TooltipButton
                title="Keep the plan renewing."
                variant="secondary"
                size="sm"
                icon={<ReplayOutlinedIcon className="icon-sm" />}
                loading={cancelDialog.busy}
                onClick={() => void changeSubscription("resume")}
                data-testid="resume-subscription"
              >
                Keep subscription
              </TooltipButton>
            ) : (
              <TooltipButton
                title={
                  cycleDate
                    ? `Stop the plan renewing. It stays as it is until ${cycleDate}.`
                    : "Stop the plan renewing. It stays as it is until the end of this cycle."
                }
                variant="secondary"
                size="sm"
                className="ml-auto"
                icon={<EventBusyOutlinedIcon className="icon-sm" />}
                onClick={openCancel}
                data-testid="cancel-subscription"
              >
                Cancel subscription
              </TooltipButton>
            )}
          </Inline>
        )}
        {billingError && !seatPurchase.open && !cancelDialog.open && (
          <span className="px-2 py-1 text-error" data-testid="billing-error">
            {billingError}
          </span>
        )}
      </section>

      <UsageSection orgId={org.org_id} usage={usage} onRefresh={refreshUsage} />

      {canSeeInvoices && (
        <section className="flex flex-col">
          {/* This list is the org's own. The customer-wide receipts page is the
              payer's, and reached from the billing details dialog. */}
          <SectionRule label="Invoices" />
          {/* Four rows: two full sections sit above this list. */}
          <div className="flex max-h-36 flex-col overflow-y-auto">
            <InvoiceHistory orgId={org.org_id} refresh={confirmedAt} />
          </div>
        </section>
      )}

      {plan && billing?.subscription && (
        <SeatsDialog
          open={canAddSeats && seatPurchase.open}
          onClose={seatPurchase.close}
          held={heldSeats}
          floor={seatPurchase.floor}
          wanted={seatPurchase.wanted}
          onWantedChange={seatPurchase.setWanted}
          plan={plan}
          seatRate={rate}
          busy={seatPurchase.busy}
          error={seatPurchase.error}
          onConfirm={confirmSeats}
          reduceOn={cycleDate}
        />
      )}
      <CardFormDialog
        transactionId={cardForm.payload}
        // The processor may have taken the card whether or not `completed` reached us.
        onClose={() => {
          cardForm.close();
          refreshBillingContext();
        }}
        onCompleted={() => {
          cardForm.close();
          confirmCardChange(org.org_id, billing, org.name);
        }}
        onSettled={() => undefined}
      />
      {billing?.billed_to && billing.subscription && (
        <BillingDetailsDialog
          open={details.open}
          onClose={details.close}
          email={billing.billed_to}
          card={billing.card}
          paidBy={billing.paid_by}
          cardOnFile={billing.card_on_file}
          // Withheld while the subscription winds down, unless the last cycle
          // went unpaid and a new card settles money owed.
          onUpdateCard={
            canActOnSubscription && (!pendingCancel || pastDue)
              ? () => {
                  details.close();
                  void openCard();
                }
              : null
          }
          onOpenPortal={canOpenPortal ? () => void openPortal() : null}
          load={loadBillingDetails}
          previewEmail={previewContact}
          busy={details.busy}
          endsOn={pendingCancel ? cycleDate : null}
          errorField={details.error?.field ?? null}
          error={details.error?.message ?? null}
          onSave={(save) => void saveDetails(save)}
        />
      )}
      {billing?.subscription && (
        <CancelSubscriptionDialog
          open={cancelDialog.open}
          onClose={cancelDialog.close}
          endsOn={
            billing.subscription.current_period_end !== null
              ? formatDayMonthYear(
                  new Date(
                    billing.subscription.current_period_end * 1000,
                  ).toISOString(),
                )
              : null
          }
          overFreeLimit={overFreeLimit}
          busy={cancelDialog.busy}
          error={billingError}
          onConfirm={() => void changeSubscription("cancel")}
        />
      )}
      <PlanUpgrade
        org={org}
        plan={plan}
        catalog={catalog}
        upgrades={upgrades}
        currentRow={currentRow}
        memberCount={memberCount}
        plans={plans}
        plansOffered={upgrades.length > 0 && !applying && !stuck}
        checkAddress={previewContact}
        onRefused={reloadBilling}
      />
    </>
  );
};
